import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import type { FormEvent } from "react";

import { claimCompany, closeClaim, extendClaim, setReminder, setWaitlist, undoClaim } from "../../api/claims";
import type { ClaimClose } from "../../api/claims";
import { errorText } from "../../api/client";
import type { AdviceAction, CompanyDetail, NoReason } from "../../api/companies";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { FormError } from "../../components/Field";
import { useToast } from "../../components/Toast";
import { nl } from "../../i18n/nl";
import { formatDate } from "../../lib/format";
import { useReport } from "../report/ReportProvider";
import { useMyClaims, useRefreshAfterChange } from "./queries";
import styles from "./claims.module.css";

const t = nl.claims;
const REASONS: readonly NoReason[] = ["it_extern", "geen_stages", "geen_begeleider", "al_volzet", "anders"];

type CloseMode = "ja" | "nee" | "ingetrokken";

function CloseClaimDialog({
  claimId,
  mode,
  onClose,
}: {
  claimId: string;
  mode: CloseMode;
  onClose: () => void;
}) {
  const id = useId();
  const toast = useToast();
  const refresh = useRefreshAfterChange();
  const [reason, setReason] = useState<NoReason | null>(null);
  const [note, setNote] = useState("");
  const [total, setTotal] = useState("");
  const [filled, setFilled] = useState("");
  const close = useMutation({
    mutationFn: (body: ClaimClose) => closeClaim(claimId, body),
    onSuccess: () => {
      refresh();
      toast({ message: mode === "ja" ? t.closedYes : mode === "nee" ? t.closedNo : t.withdrawn });
      onClose();
    },
  });

  const valid = mode !== "nee" || (reason !== null && (reason !== "anders" || note.trim() !== ""));

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid) return;
    if (mode === "ja") {
      const spots = {
        ...(total ? { spots_total: Number(total) } : {}),
        ...(filled ? { spots_filled: Number(filled) } : {}),
      };
      close.mutate({
        outcome: "ja",
        report: Object.keys(spots).length ? { ...spots, anonymous: true, confirm: false } : null,
      });
    } else if (mode === "nee") {
      close.mutate({
        outcome: "nee",
        report: {
          reason,
          ...(reason === "anders" ? { reason_note: note.trim() } : {}),
          anonymous: true,
          confirm: false,
        },
      });
    } else {
      close.mutate({ outcome: "ingetrokken" });
    }
  }

  const title = mode === "ja" ? t.closeYesTitle : mode === "nee" ? t.closeNoTitle : t.closeWithdraw;
  const text = mode === "ja" ? t.closeYesText : mode === "nee" ? t.closeNoText : t.closeWithdrawText;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={title}
      description={text}
    >
      <form className={styles.closeForm} onSubmit={submit} noValidate>
        {mode === "ja" && (
          <div className={styles.twoColumns}>
            <label className={styles.field} htmlFor={`${id}-total`}>
              <span>{nl.report.spotsTotal}</span>
              <input
                id={`${id}-total`}
                type="number"
                min={1}
                max={50}
                inputMode="numeric"
                value={total}
                onChange={(event) => {
                  setTotal(event.target.value);
                }}
              />
            </label>
            <label className={styles.field} htmlFor={`${id}-filled`}>
              <span>{nl.report.spotsFilled}</span>
              <input
                id={`${id}-filled`}
                type="number"
                min={0}
                max={50}
                inputMode="numeric"
                value={filled}
                onChange={(event) => {
                  setFilled(event.target.value);
                }}
              />
            </label>
          </div>
        )}
        {mode === "nee" && (
          <fieldset className={styles.reasons}>
            <legend className="sr-only">{nl.report.reason}</legend>
            {REASONS.map((option) => (
              <label key={option} className={styles.reason} htmlFor={`${id}-${option}`}>
                <input
                  id={`${id}-${option}`}
                  type="radio"
                  name={`${id}-reason`}
                  checked={reason === option}
                  onChange={() => {
                    setReason(option);
                  }}
                />
                <span>{nl.report.reasons[option]}</span>
              </label>
            ))}
            {reason === "anders" && (
              <label className={styles.field} htmlFor={`${id}-note`}>
                <span>{nl.report.reasonNote}</span>
                <input
                  id={`${id}-note`}
                  maxLength={140}
                  value={note}
                  onChange={(event) => {
                    setNote(event.target.value);
                  }}
                />
              </label>
            )}
          </fieldset>
        )}
        <FormError message={close.error ? errorText(close.error) : null} />
        <div className={styles.actions}>
          <Button onClick={onClose}>{t.cancel}</Button>
          <Button type="submit" variant="primary" disabled={!valid || close.isPending}>
            {t.closeSubmit}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** The advice card's buttons: claim, close, extend, waitlist, reminder, report (M3-05). */
export function AdviceActions({ company }: { company: CompanyDetail }) {
  const toast = useToast();
  const openReport = useReport();
  const refresh = useRefreshAfterChange();
  const claims = useMyClaims();
  const [closing, setClosing] = useState<CloseMode | null>(null);
  const { primary, secondary, disabled_reason: disabled } = company.advice;
  const ownClaim = claims.data?.items.find((c) => c.company_id === company.id);

  const fail = (error: unknown) => {
    toast({ message: errorText(error) });
  };
  const claim = useMutation({
    mutationFn: () => claimCompany(company.id),
    onSuccess: (created) => {
      refresh();
      toast({
        message: t.claimed(company.name, formatDate(created.expires_at)),
        onUndo: () => {
          void undoClaim(created.id).then(() => {
            refresh();
            toast({ message: t.unclaimed });
          }, fail);
        },
      });
    },
    onError: fail,
  });
  const extend = useMutation({
    mutationFn: (claimId: string) => extendClaim(claimId),
    onSuccess: (updated) => {
      refresh();
      // Extending cannot be undone (one extension per claim, docs/PRODUCT.md).
      toast({ message: t.extended(formatDate(updated.expires_at)) });
    },
    onError: fail,
  });
  const toggle = useMutation({
    mutationFn: ({ kind, on }: { kind: "waitlist" | "reminder"; on: boolean }) =>
      kind === "waitlist" ? setWaitlist(company.id, on) : setReminder(company.id, on),
    onSuccess: (_, { kind, on }) => {
      refresh();
      const message =
        kind === "waitlist" ? (on ? t.joined : t.left) : on ? t.reminderOn : t.reminderOff;
      toast({
        message,
        onUndo: () => {
          toggle.mutate({ kind, on: !on });
        },
      });
    },
    onError: fail,
  });
  const busy = claim.isPending || extend.isPending || toggle.isPending;

  function run(action: AdviceAction) {
    switch (action) {
      case "claim":
        claim.mutate();
        break;
      case "extend":
        if (ownClaim) extend.mutate(ownClaim.id);
        break;
      case "close_yes":
        setClosing("ja");
        break;
      case "close_no":
        setClosing("nee");
        break;
      case "join_waitlist":
        toggle.mutate({ kind: "waitlist", on: true });
        break;
      case "leave_waitlist":
        toggle.mutate({ kind: "waitlist", on: false });
        break;
      case "remind_next_year":
        toggle.mutate({ kind: "reminder", on: true });
        break;
      case "cancel_reminder":
        toggle.mutate({ kind: "reminder", on: false });
        break;
      case "report":
        openReport(company.id);
        break;
    }
  }

  const button = (action: AdviceAction, variant: "primary" | "secondary" | "ghost") => (
    <Button
      key={action}
      variant={variant}
      disabled={busy || (variant === "primary" && disabled !== null)}
      onClick={() => {
        run(action);
      }}
    >
      {nl.companies.actions[action]}
    </Button>
  );

  return (
    <>
      {primary && button(primary, "primary")}
      {secondary.map((action) => button(action, action === "report" ? "ghost" : "secondary"))}
      {company.is_my_claim && ownClaim && (
        <Button
          variant="ghost"
          disabled={busy}
          onClick={() => {
            setClosing("ingetrokken");
          }}
        >
          {t.closeWithdraw}
        </Button>
      )}
      {closing && ownClaim && (
        <CloseClaimDialog
          claimId={ownClaim.id}
          mode={closing}
          onClose={() => {
            setClosing(null);
          }}
        />
      )}
    </>
  );
}
